/**
 * FestivalToast — subtle bottom-of-screen toast for festival lifecycle events.
 *
 * AGE-1199: listens for MSG_FESTIVAL_STATE_CHANGED on the Colyseus room and
 * renders a non-intrusive text overlay that fades in/out. No popup, no modal.
 *
 * The toast also plays a soft chime via OverworldAudioManager and emits a
 * journal event onto the game-event bus so JournalScene can record the entry.
 */
import * as Phaser from 'phaser';
import type { FestivalStateChangedPayload } from '@game/shared';
import { MSG_FESTIVAL_STATE_CHANGED } from '@game/shared';
import type { OverworldAudioManager } from '../audio/OverworldAudioManager';

/** Duration (ms) the toast text is fully visible before starting to fade out. */
const TOAST_HOLD_MS = 3500;
/** Duration (ms) of the fade-in and fade-out tween. */
const TOAST_FADE_MS = 800;
/** Y offset from the bottom of the camera viewport. */
const TOAST_BOTTOM_OFFSET = 64;
/** Font size for the toast text. */
const TOAST_FONT_SIZE = '15px';
/** Toast text color — warm parchment. */
const TOAST_COLOR = '#f5e6c8';

/** Copy shown per festival phase. */
function getToastText(payload: FestivalStateChangedPayload): string | null {
  switch (payload.phase) {
    case 'telegraph':
      return 'The village board speaks of an upcoming gathering\u2026';
    case 'festival_day':
      return `The ${formatFestivalName(payload.festivalType)} has begun!`;
    default:
      return null;
  }
}

/** Journal prose per festival phase. */
function getJournalText(payload: FestivalStateChangedPayload): string | null {
  switch (payload.phase) {
    case 'telegraph':
      return `Whispers travel through ${payload.hostRealm} — a ${formatFestivalName(payload.festivalType)} draws near.`;
    case 'festival_day':
      return `The ${formatFestivalName(payload.festivalType)} has begun in ${payload.hostRealm}! The air hums with celebration.`;
    default:
      return null;
  }
}

/** Convert a snake_case or camelCase festival type to display name. */
function formatFestivalName(raw: string): string {
  return raw
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export class FestivalToast {
  private scene: Phaser.Scene;
  private audio: OverworldAudioManager | null;
  private toastText: Phaser.GameObjects.Text | null = null;
  private activeTween: Phaser.Tweens.Tween | null = null;
  private room: { onMessage: (type: string, cb: (payload: unknown) => void) => void } | null;

  constructor(
    scene: Phaser.Scene,
    audio: OverworldAudioManager | null,
    room: { onMessage: (type: string, cb: (payload: unknown) => void) => void } | null,
  ) {
    this.scene = scene;
    this.audio = audio;
    this.room = room;
    this.wireListener();
  }

  private wireListener(): void {
    if (!this.room) return;
    this.room.onMessage(MSG_FESTIVAL_STATE_CHANGED, (raw: unknown) => {
      const payload = raw as FestivalStateChangedPayload;
      this.handleFestivalStateChanged(payload);
    });
  }

  private handleFestivalStateChanged(payload: FestivalStateChangedPayload): void {
    const text = getToastText(payload);
    if (!text) return;

    // Play chime
    this.audio?.playFestivalChime(payload.phase);

    // Show toast
    this.showToast(text);

    // Emit journal entry onto game-event bus
    const journalText = getJournalText(payload);
    if (journalText) {
      this.scene.game.events.emit('festival:journal-entry', {
        festivalId: payload.festivalId,
        festivalType: payload.festivalType,
        phase: payload.phase,
        hostRealm: payload.hostRealm,
        text: journalText,
        timestamp: Date.now(),
      });
    }
  }

  private showToast(message: string): void {
    // If a toast is already showing, kill it
    if (this.activeTween) {
      this.activeTween.destroy();
      this.activeTween = null;
    }
    if (this.toastText) {
      this.toastText.destroy();
      this.toastText = null;
    }

    const cam = this.scene.cameras.main;
    const x = cam.width / 2;
    const y = cam.height - TOAST_BOTTOM_OFFSET;

    this.toastText = this.scene.add.text(x, y, message, {
      fontSize: TOAST_FONT_SIZE,
      color: TOAST_COLOR,
      fontFamily: 'serif',
      align: 'center',
      shadow: {
        offsetX: 1,
        offsetY: 1,
        color: '#000000',
        blur: 3,
        fill: true,
        stroke: true,
      },
    });
    this.toastText.setOrigin(0.5, 0.5);
    this.toastText.setAlpha(0);
    this.toastText.setDepth(200); // Above HUD
    this.toastText.setScrollFactor(0); // Camera-fixed

    // Fade in → hold → fade out
    this.activeTween = this.scene.tweens.add({
      targets: this.toastText,
      alpha: { from: 0, to: 1 },
      duration: TOAST_FADE_MS,
      ease: 'Sine.easeIn',
      hold: TOAST_HOLD_MS,
      yoyo: true,
      onComplete: () => {
        this.toastText?.destroy();
        this.toastText = null;
        this.activeTween = null;
      },
    });
  }

  destroy(): void {
    if (this.activeTween) {
      this.activeTween.destroy();
      this.activeTween = null;
    }
    if (this.toastText) {
      this.toastText.destroy();
      this.toastText = null;
    }
    this.room = null;
    this.audio = null;
  }
}
